import Dexie from 'dexie';

// IndexedDB database for local storage
export const db = new Dexie('AttendanceTrackerDB');

db.version(1).stores({
  settings: 'key, value',
  attendance: 'date, status, synced',
  holidays: 'date, name, localName, countryCode'
});

// Database helper functions
export class DatabaseService {
  // Settings
  static async getSetting(key) {
    const setting = await db.settings.get(key);
    return setting?.value;
  }

  static async setSetting(key, value) {
    await db.settings.put({ key, value });
  }

  // User Info
  static async getUserInfo() {
    const name = await this.getSetting('userName');
    const email = await this.getSetting('userEmail');
    const countryCode = await this.getSetting('countryCode');
    const userId = await this.getSetting('userId');
    return { name, email, countryCode, userId };
  }

  static async setUserInfo(name, email, countryCode) {
    await this.setSetting('userName', name);
    await this.setSetting('userEmail', email);
    await this.setSetting('countryCode', countryCode || 'IE');
  }

  static async setUserId(userId) {
    await this.setSetting('userId', userId);
  }

  // Attendance Records
  static async getAttendance(date) {
    return await db.attendance.get(date);
  }

  static async setAttendance(date, status) {
    await db.attendance.put({
      date,
      status,
      synced: false,
      updatedAt: new Date().toISOString()
    });
  }

  static async getAttendanceRange(startDate, endDate) {
    if (!startDate || !endDate) {
      console.warn('getAttendanceRange called with invalid dates:', { startDate, endDate });
      return [];
    }

    // Validate that dates are strings
    if (typeof startDate !== 'string' || typeof endDate !== 'string') {
      console.error('getAttendanceRange requires string dates:', { startDate, endDate });
      return [];
    }

    try {
      return await db.attendance
        .where('date')
        .between(startDate, endDate, true, true)
        .toArray();
    } catch (error) {
      console.error('Error in getAttendanceRange:', error, { startDate, endDate });
      return [];
    }
  }

  static async getAllAttendance() {
    return await db.attendance.toArray();
  }

  static async getUnsyncedAttendance() {
    return await db.attendance.where('synced').equals(false).toArray();
  }

  static async markAttendanceSynced(dates) {
    await db.attendance.bulkUpdate(
      dates.map(date => ({
        key: date,
        changes: { synced: true }
      }))
    );
  }

  static async bulkSetAttendance(records) {
    await db.attendance.bulkPut(records);
  }

  // Holidays
  static async getHoliday(date) {
    return await db.holidays.get(date);
  }

  static async setHolidays(holidays) {
    const formatted = holidays.map(h => ({
      date: h.date,
      name: h.name,
      localName: h.localName,
      countryCode: h.countryCode
    }));
    await db.holidays.bulkPut(formatted);
  }

  static async getHolidaysForYear(year) {
    if (!year) {
      console.warn('getHolidaysForYear called with invalid year:', year);
      return [];
    }

    // Validate that year is a number
    if (typeof year !== 'number' || isNaN(year)) {
      console.error('getHolidaysForYear requires a valid year number:', year);
      return [];
    }

    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;

    try {
      return await db.holidays
        .where('date')
        .between(startDate, endDate, true, true)
        .toArray();
    } catch (error) {
      console.error('Error in getHolidaysForYear:', error, { year, startDate, endDate });
      return [];
    }
  }

  static async clearHolidays() {
    await db.holidays.clear();
  }

  // Check if setup is complete
  static async isSetupComplete() {
    const name = await this.getSetting('userName');
    const email = await this.getSetting('userEmail');
    const countryCode = await this.getSetting('countryCode');
    return !!(name && email && countryCode);
  }

  // Working days configuration
  static async getWorkingDays() {
    const workingDays = await this.getSetting('workingDays');
    // Default: Monday to Friday (1-5, where 0=Sunday, 6=Saturday)
    return workingDays || [1, 2, 3, 4, 5];
  }

  static async setWorkingDays(days) {
    await this.setSetting('workingDays', days);
  }

  static async isWorkingDay(dayOfWeek) {
    const workingDays = await this.getWorkingDays();
    return workingDays.includes(dayOfWeek);
  }
}
